import { PageControlTabWithIndependantSaving } from '../../../webmodule-common/other/ui/data-entry-screen-base';
import { PromiseTemplate, Snippet } from '../../../webmodule-common/interop/types/misc';

import { tlang } from '../../../webmodule-common/other/language/lang';
import { html, TemplateResult } from 'lit';
import {
  WebModuleLitTable,
  WebModuleLitTableColumnDef
} from '../../../webmodule-common/components/src/components/table/lit-table';
import { property, state } from 'lit/decorators.js';
import {
  DataContainer,
  DealerDeploymentSupplierFlatView,
  DealerDeploymentTenantFlatView
} from '../../supplier/data-container';
import { getApiFactory } from '../../api/api-injector';
import {
  DealerDeploymentOperationType,
  DeploymentType,
  SystemViewDeployment,
  TenantInitializationState,
  TenantLogin,
  TenantLoginDeploymentConfig,
  ViewBaseLogin
} from '../../api/api-interface-licenseserver';
import { LitElementBase } from '../../../webmodule-common/other/litelement-base';
import { FormInputAssistantWithResolver } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant';
import { localDateTimeToServer } from '../../../webmodule-common/other/datetime-converter';
import { IUserClaims, userClaims } from '../../../webmodule-common/other/currentuser-claims';
import { getClaimsWrapper } from '../../../webmodule-common/other/api/user-security-impl';
import { FieldType } from '../../../webmodule-common/other/ui/databinding/data-tracker';
import { fireQuickInformationToast } from '../../../webmodule-common/other/toast-away';
import { autoElement } from '../../../webmodule-common/components/src/common/lit-decorator-extended';

interface StaffInfo {
  tenantLogin: TenantLogin;
  config: TenantLoginDeploymentConfig;
  claims: IUserClaims;
}

@autoElement()
export class DeploymentAccessAssignmentView extends PageControlTabWithIndependantSaving {
  @state()
  private selectedStaffMember?: StaffInfo;

  get selectedStaffMemberId(): number {
    return this.selectedStaffMember?.tenantLogin.baseLoginId ?? 0;
  }

  @state()
  private staffColumns: WebModuleLitTableColumnDef[];
  @state()
  private data: DataContainer | null = null;
  @state()
  private staffData: StaffInfo[] = [];
  @state()
  private deploymentView: SystemViewDeployment | null = null;
  @state()
  private deploymentColumns: WebModuleLitTableColumnDef[];
  @state()
  private deploymentData: DealerDeploymentSupplierFlatView[] = [];
  private forms = new FormInputAssistantWithResolver(
    this,
    () => this.selectedStaffMember === undefined || this.isReadonly,
    f => {
      const regex = /access-deployment-(\d+)-tenant-(\d+)/;

      f.immediateBindingUpdate = true;
      f.getter = (fieldname: string) => {
        const match = fieldname.match(regex);
        if (match) {
          const deploymentId = parseInt(match[1]);
          const tenantId = parseInt(match[2]);
          const deployment = this.data?.data?.cynclyLoginTenantAccesses
            .find(x => x.baseLoginId === this.selectedStaffMemberId)
            ?.access.deployments.find(d => d.deploymentId === deploymentId);
          return tenantId === 0 ? (deployment?.allTenants ?? false) : (deployment?.tenants.includes(tenantId) ?? false);
        } else {
          return false;
        }
      };
      f.setter = (fieldname: string, value: boolean) => {
        let valueChanged = false;
        const match = fieldname.match(regex);
        if (!this.data?.data) return;
        if (match) {
          const deploymentId = parseInt(match[1]);
          const tenantId = parseInt(match[2]);
          let staffContent = this.data.data.cynclyLoginTenantAccesses.find(
            x => x.baseLoginId === this.selectedStaffMemberId
          );
          if (!staffContent) {
            valueChanged = true;
            staffContent = {
              id: 0,
              dateCreated: localDateTimeToServer(new Date()),
              recordVersion: '',
              baseLoginId: this.selectedStaffMemberId!,
              access: {
                deployments: []
              }
            };
            this.data.data.cynclyLoginTenantAccesses.push(staffContent);
          }
          let deployment = staffContent.access.deployments.find(d => d.deploymentId === deploymentId);
          if (!deployment) {
            valueChanged = true;
            deployment = {
              deploymentId,
              allTenants: false,
              tenants: []
            };
            staffContent.access.deployments.push(deployment);
          }
          if (tenantId === 0) {
            valueChanged = valueChanged || deployment.allTenants !== value;
            deployment.allTenants = value;
          } else {
            valueChanged =
              valueChanged || (value ? !deployment.tenants.includes(tenantId) : deployment.tenants.includes(tenantId));
            deployment.tenants = deployment.tenants.filter(x => x !== tenantId);
            if (value) deployment.tenants.push(tenantId);
          }
          if (deployment.allTenants) deployment.tenants = [];
          const data = this.data.data;
          if (valueChanged)
            getApiFactory()
              .licenseServer()
              .saveCynclyLoginTenantAccesses({
                access: staffContent.access,
                baseLoginId: staffContent.baseLoginId
              })
              .then(result => {
                if (result) {
                  const index = data.cynclyLoginTenantAccesses.findIndex(
                    x => x.baseLoginId === this.selectedStaffMemberId
                  );
                  if (index >= 0) {
                    Object.assign(data.cynclyLoginTenantAccesses[index], result);
                  } else {
                    data.cynclyLoginTenantAccesses.push(result);
                  }
                }
                fireQuickInformationToast('Access saved');
                this.requestUpdate();
              });
        }
      };
    }
  );
  private logins: ViewBaseLogin[] | null;

  getDeploymentTypeBadge(deploymentType: DeploymentType): TemplateResult {
    let badgeText = '';
    let badgevariant = 'primary';
    switch (deploymentType) {
      case DeploymentType.SingleSupplierInterface:
      case DeploymentType.SupplierInterface:
        badgevariant = 'neutral';
        badgeText = 'Supplier Portal';
        break;
      default:
        badgeText = 'Dealer Portal';
        badgevariant = 'primary';
    }
    return html` <webmodule-badge .variant=${badgevariant}
      ><span style="text-align:center; width:100px;">${badgeText}</span></webmodule-badge
    >`;
  }

  getOperationTypeBadge(deploymentType: DealerDeploymentOperationType): TemplateResult {
    const badgeText = DealerDeploymentOperationType[deploymentType];
    let badgevariant = 'primary';
    switch (deploymentType) {
      case DealerDeploymentOperationType.Development:
        badgevariant = 'primary';
        break;
      case DealerDeploymentOperationType.Test:
        badgevariant = 'neutral';
        break;
      case DealerDeploymentOperationType.Production:
        badgevariant = 'success';
        break;
    }
    return html` <webmodule-badge .variant=${badgevariant}
      ><span style="text-align:center; width:100px;">${badgeText}</span></webmodule-badge
    >`;
  }

  constructor() {
    super();
    this.pageFragment = 'staff-deployment-access-assignment';

    this.staffColumns = [
      {
        title: 'Active',
        fieldName: 'userName',
        classes: 'colpx-150 colpxmax-150',
        displayValue: (_table: WebModuleLitTable, item: StaffInfo) => {
          return item.config.active ? 'Yes' : 'No';
        }
      },
      {
        title: 'User Name',
        fieldName: 'userName',
        classes: 'colpx-150 colpxmax-150',
        displayValue: (_table: WebModuleLitTable, item: StaffInfo) => {
          return item.tenantLogin.baseLogin.userName;
        }
      },
      {
        title: 'Name',
        fieldName: 'friendlyName',
        classes: 'colpx-150 colpxmax-150',
        displayValue: (_table: WebModuleLitTable, item: StaffInfo) => {
          return item.tenantLogin.baseLogin.friendlyName;
        }
      }
    ];

    this.deploymentColumns = [
      {
        title: () =>
          html` <webmodule-icon
            id="quote-item-expand-all-indicator"
            library="fa"
            name="fas-angles-down"
          ></webmodule-icon>`,
        fieldName: 'type',
        classes: 'colpx-35 colpxmax-35',
        displayValue: (_table: WebModuleLitTable, item: DealerDeploymentSupplierFlatView) => {
          const name = _table.isExpanded(item) ? 'fas-caret-up' : 'fas-caret-down';
          return html` <webmodule-icon
            id="quote-item-expand-all-indicator"
            library="fa"
            name=${name}
          ></webmodule-icon>`;
        },
        click: (_e: Event, table: WebModuleLitTable, item: DealerDeploymentSupplierFlatView) => {
          if (table.isExpanded(item)) {
            table.remExtension(item);
          } else {
            table.addExtension(item, this.deploymentExtensionGenerator);
          }
          return true;
        }
      },
      {
        title: () => html`<span class="">System</span>`,
        fieldName: 'type',
        classes: 'colpx-400 colpxmax-400 flex-wrap overflow-visible',
        displayValue: (_table: WebModuleLitTable, item: DealerDeploymentSupplierFlatView) => {
          return html` <div class="flex-fullwidth">
              ${this.getOperationTypeBadge(item.dealerDeployment.operationType)}${this.getDeploymentTypeBadge(
                item.dealerDeployment.deploymentType
              )}
            </div>
            <div class="flex-fullwidth"><span>${item.dealerDeployment.uiDomain}</span></div>`;
        }
      },
      {
        title: () => html`<span class="">Full Site Tenant Access</span>`,
        fieldName: 'type',
        classes: 'colpx-330 colpxmax-330',
        displayValue: (_table: WebModuleLitTable, item: DealerDeploymentSupplierFlatView) => {
          if (!this.selectedStaffMember) return '';
          if (this.selectedStaffMember?.claims.isLicenseServerAdmin) {
            return `Software Admin has full access`;
          }
          if (this.selectedStaffMember?.claims.isSuppportManager) {
            return `Support Manager has full access`;
          }
          if (this.selectedStaffMember?.claims.isSupportTenantAllAccess) {
            return `User has full tenant access`;
          }
          return this.forms.switch(
            `access-deployment-${item.dealerDeployment.id}-tenant-0-supplierinstance-${item.supplierInstance.id}`,
            undefined
          );
        }
      }
    ];
  }

  private getGlobalSupplierTemplate(item: DealerDeploymentSupplierFlatView) {
    return html` <div class="global-supplier-section">
      <div class="global-supplier-header">Supplier: ${item.globalSupplier.displayName}</div>
    </div>`;
  }

  private beforeDeploymentRowRenderEvent = (
    e: CustomEvent<{ item: DealerDeploymentSupplierFlatView; index: number; template?: TemplateResult }>
  ) => {
    const details = e.detail;
    let buildTemplate = details.index === 0;
    if (!buildTemplate) {
      const previousValue = this.deploymentData[details.index - 1];
      buildTemplate = previousValue.globalSupplier !== details.item.globalSupplier;
    }

    if (buildTemplate) {
      details.template = this.getGlobalSupplierTemplate(details.item);
    }
  };
  private deploymentExtensionGenerator = (item: DealerDeploymentSupplierFlatView) => {
    const staffContent = this.data?.data.cynclyLoginTenantAccesses.find(
      x => x.baseLoginId === this.selectedStaffMemberId
    );
    const allTenants =
      staffContent?.access.deployments.find(d => d.deploymentId === item.dealerDeployment.id)?.allTenants ?? false;

    return html` <div class="deployment-expansion-detail">
      <wm-staffaccesstenantassignment
        .fullTenantAccess=${allTenants}
        .readonly=${this.selectedStaffMember === undefined}
        .baseLoginId=${this.selectedStaffMemberId}
        .data=${this.data}
        .deploymentId=${item.dealerDeployment.id}
        .globalSupplierId=${item.globalSupplier.id}
        .forms=${this.forms}
        .userClaims=${this.selectedStaffMember?.claims}
        .supplierInstanceId=${item.supplierInstance.id}
      ></wm-staffaccesstenantassignment>
    </div>`;
  };

  public allowDeletePage(): boolean {
    return false;
  }

  async prepareForSave(): Promise<void> {
    //
  }

  public internalDataChanged(): boolean {
    return false;
  }

  async reloadData() {
    this.forms.clear();
    const api = getApiFactory().licenseServer();
    this.logins = (await api.getTenantLogins())?.logins ?? [];
    const data = await api.getFullSupplierNetwork();
    if (data) {
      this.data = new DataContainer(data);
      this.deploymentData = this.data.flatDataView();
      this.forms.clear();
      for (const item of this.deploymentData) {
        this.forms.add(
          `access-deployment-${item.dealerDeployment.id}-tenant-0-supplierinstance-${item.supplierInstance.id}`,
          FieldType.boolean,
          false
        );
        for (const tenantConfig of item.tenantConfig) {
          this.forms.add(
            `access-deployment-${item.dealerDeployment.id}-tenant-${tenantConfig.tenantId}-supplierinstance-${item.supplierInstance.id}`,
            FieldType.boolean,
            false
          );
        }
      }
      const dv = await api.getDeploymentView(globalThis.dealerConfiguration.dealerDeploymentId);
      this.deploymentView = dv;
      if (dv)
        this.staffData =
          this.deploymentView?.tenantLogin.map(x => {
            const item: StaffInfo = {
              tenantLogin: x,
              config: dv.tenantLoginDeploymentConfig.find(y => y.tenantLoginId === x.id)!,
              claims: userClaims(
                x.baseLogin.systemUserId,
                getClaimsWrapper(this.logins?.find(l => l.baseLogin.id === x.baseLogin.id)?.allClaims ?? {})
              )
            };
            return item;
          }) ?? [];
    }
    this.refreshParent();
  }

  async onEnter(): Promise<void> {
    await this.reloadData();
  }

  public buttonMenu(): Snippet {
    return html``;
  }

  protected getCaption(): Snippet {
    return tlang`Staff Deployment Access Assignment`;
  }

  protected async internalSaveData(): Promise<boolean> {
    //nothing to do
    return true;
  }

  protected async bodyTemplate(): PromiseTemplate {
    const getRowClass = (item: StaffInfo) => {
      if (this.selectedStaffMemberId === item.tenantLogin.baseLoginId) {
        return 'staff selected';
      }

      return 'staff';
    };
    return html` <div class="container">
      <div class="row">
        <div class="col-6">
          <webmodule-lit-table
            clickrows=${true}
            id="staff-table"
            class="data-table"
            .rowClass=${'row'}
            .colClass=${'col'}
            .data=${[...this.staffData]}
            .columns=${this.staffColumns}
            pageLength=${10000}
            .itemRowClassEvent=${getRowClass}
            @rowclick=${(e: CustomEvent<{ item: StaffInfo }>) => {
              this.selectedStaffMember = e.detail.item;
            }}
          ></webmodule-lit-table>
        </div>
        <div class="col-6">
          <webmodule-lit-table
            id="table"
            class="data-table"
            .rowClass=${'row'}
            .colClass=${'col'}
            .data=${[...this.deploymentData]}
            .columns=${this.deploymentColumns}
            pageLength=${10000}
            ?clickrows=${false}
            @before-row-render=${this.beforeDeploymentRowRenderEvent}
          >
          </webmodule-lit-table>
        </div>
      </div>
    </div>`;
  }
}

@autoElement()
export class StaffAccessTenantAssignment extends LitElementBase {
  @property({ type: Number })
  public baseLoginId: number;
  @property({ type: Object })
  public data!: DataContainer;
  @property({ type: Number })
  public deploymentId!: number;
  @property({ type: Number })
  public supplierInstanceId!: number;
  @property({ type: Number })
  public globalSupplierId?: number;
  @state()
  private tableColumns: WebModuleLitTableColumnDef[] = [];
  @state()
  private tableData: DealerDeploymentTenantFlatView[] = [];
  @property({ type: Object })
  public forms!: FormInputAssistantWithResolver;
  @property({ type: Object })
  public userClaims?: IUserClaims;
  @property({ type: Boolean })
  public fullTenantAccess: boolean = false;

  firstUpdated() {
    this.tableData = this.data.flatTenantView(this.deploymentId, this.globalSupplierId);
  }

  constructor() {
    super();
    this.tableColumns = [
      {
        title: () => html`<span class="">Tenant Id</span>`,
        classes: 'colpx-100 colpxmax-100',
        fieldName: 'tenant.id',
        displayValue: (_table: WebModuleLitTable, item: DealerDeploymentTenantFlatView) => {
          return html`<span>${item.tenant.id}</span>`;
        }
      },
      {
        title: () => html`<span class="">Name</span>`,
        classes: 'colpx-250 colpxmax-250',
        fieldName: 'tenant.companyName',
        displayValue: (_table: WebModuleLitTable, item: DealerDeploymentTenantFlatView) => {
          return html`<span>${item.tenant.companyName}</span>`;
        }
      },
      {
        title: () => html`<span class="">State</span>`,
        classes: 'colpx-100 colpxmax-100',
        fieldName: 'tenantConfig.state',
        displayValue: (_table: WebModuleLitTable, item: DealerDeploymentTenantFlatView) => {
          return html`<span>${TenantInitializationState[item.tenantConfig.state]}</span>`;
        }
      },
      {
        title: () => html`<span class="">Allowed Access</span>`,
        classes: 'colpx-300 colpxmax-300',
        fieldName: '',
        displayValue: (_table: WebModuleLitTable, item: DealerDeploymentTenantFlatView) => {
          if (!this.userClaims) return ``;
          if (this.userClaims.isLicenseServerAdmin) {
            return `Software Admin has full access`;
          }
          if (this.userClaims.isSuppportManager) {
            return `Support Manager has full access`;
          }
          if (this.userClaims.isSupportTenantAllAccess) {
            return `User has full tenant access`;
          }
          if (this.fullTenantAccess) {
            return `All Access Granted`;
          }
          return this.forms.switch(
            `access-deployment-${item.dealerDeployment.id}-tenant-${item.tenant.id}-supplierinstance-${this.supplierInstanceId}`
          );
        }
      }
    ];
  }

  render() {
    return html` <div>
      <webmodule-lit-table
        id="table"
        class="data-table"
        .rowClass=${'row'}
        .colClass=${'col'}
        .data=${[...this.tableData]}
        .columns=${this.tableColumns}
        pageLength=${10000}
        ?clickrows=${false}
      >
      </webmodule-lit-table>
    </div>`;
  }
}
